import { baseURlNoAPI, baseURL } from '../network/baseUrl';
import http from '../network/HttpService';

const isAbsoluteUrl = (value) =>
  typeof value === 'string' && /^https?:\/\//i.test(value);

/**
 * Build a usable media URL from either a relative path or an already-absolute URL.
 */
export const resolveMediaUrl = (pathOrUrl) => {
  if (!pathOrUrl) return null;
  if (isAbsoluteUrl(pathOrUrl)) return pathOrUrl;
  const path = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
  return `${baseURlNoAPI}${path}`;
};

/**
 * Resolve document URL for attachments (S3 presigned, absolute, or local path).
 */
export const getAttachmentDocumentUrl = (attachment) => {
  if (!attachment) return null;
  if (attachment.download_url) return attachment.download_url;
  return resolveMediaUrl(attachment.document);
};

/**
 * Resolve attachment image URLs with fallbacks when variants are not yet backfilled.
 */
export const getAttachmentLargeUrl = (attachment) => {
  if (!attachment) return null;
  if (attachment.image_url) return attachment.image_url;
  if (attachment.image) return resolveMediaUrl(attachment.image);
  return null;
};

export const getAttachmentMediumUrl = (attachment) => {
  if (!attachment) return null;
  if (attachment.image_medium_url) return attachment.image_medium_url;
  if (attachment.image_medium) return resolveMediaUrl(attachment.image_medium);
  return getAttachmentLargeUrl(attachment);
};

export const getAttachmentThumbUrl = (attachment) => {
  if (!attachment) return null;
  if (attachment.image_thumb_url) return attachment.image_thumb_url;
  if (attachment.image_thumb) return resolveMediaUrl(attachment.image_thumb);
  return getAttachmentLargeUrl(attachment);
};

export const getImageAttachments = (asset) => {
  if (!asset?.attachments?.length) return [];

  return asset.attachments.filter(
    (item) =>
      getAttachmentLargeUrl(item) ||
      getAttachmentMediumUrl(item) ||
      getAttachmentThumbUrl(item)
  );
};

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

/**
 * Fetch attachment image via API proxy (avoids S3 CORS issues for canvas/PDF export).
 */
export const fetchAttachmentImageDataUrl = async (
  attachmentId,
  variant = 'large'
) => {
  const response = await http.get(
    `${baseURL}/attachments/${attachmentId}/image/`,
    {
      params: { variant },
      withAuth: true,
      responseType: 'blob',
    }
  );

  return blobToDataUrl(response.data);
};
